import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './dialog';
import { Button } from './button';
import { Badge } from './badge';
import { Check, Clock, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { usePricing } from '../../context/PricingContext';
import {
  computeAnnualSavingsPercent,
  DEFAULT_PLAN_KEY,
} from '../../utils/pricingDisplay';
import { GROUPS_PLAN } from '../../constants/groupsPlan';
import { goToTalkToSales } from '../../constants/publicSite';

const TIER_FEATURES = {
  starter: [
    'AI SOAP notes (50/month)',
    'Live transcription & diarization',
    'Multilingual + HIPAA BAA',
    '30-day note history',
  ],
  professional: [
    'Unlimited notes',
    'Magic Editor & HPI modes',
    'Everything in Starter',
    // 'Optional EMR input ($1.99/chart)',
    '7-day free trial',
  ],
  // keep elite features for reference
  // elite: [
  //   'Everything in Professional',
  //   'Early access to new features',
  //   'Priority feature requests',
  //   'EMR input at $1.29/chart',
  //   'Same-day EMR turnaround',
  // ],
};

// ── Free plan (only shown to non-premium users) ──────────────
const FREE_PLAN = {
  key: 'free',
  name: 'Free',
  description: 'Get started with AI scribe essentials',
  price: 0,
  features: [
    'Unlimited SOAP notes',
    'Live transcription & diarization',
    'Basic note history',
  ],
};

// Elite replaced with Group plan — group is sourced from GROUPS_PLAN
const PAID_TIER_ORDER = ['starter', 'professional', GROUPS_PLAN.key];
const FREE_TIER_KEY = 'free';

// Short labels used inside CTA copy (Upgrade to Pro, Switch to Starter…)
const PLAN_SHORT_LABEL = {
  free: 'Free',
  starter: 'Starter',
  professional: 'Pro',
};

const CURRENCY_SYMBOL = '$'; // ← change to '₹' if you want rupees

// ── CTA palette (kept exactly as your current cards) ─────────
const CTA_GRADIENT =
  'bg-gradient-to-r from-blue-600 to-teal-600 text-white hover:shadow-lg transition-all';

// Current-plan button — GPT-style gray, non-clickable look
const CURRENT_BUTTON =
  'bg-gray-100 text-gray-700 border border-gray-200 cursor-default hover:bg-gray-100';

const VBotSubscriptionModal = ({
  isOpen,
  onClose,
  onSubscribe,
  onContactSales,
  subscriptionInfo = null,
  onRefreshSubscription,
}) => {
  const navigate = useNavigate();
  const { getAmount, getDescription, getPlanMeta } = usePricing();

  const isPremium = subscriptionInfo?.planType === 'premium';
  const currentPlanKey = subscriptionInfo?.planKey || DEFAULT_PLAN_KEY;
  const currentBillingPeriod = subscriptionInfo?.billingPeriod || null;
  const isOnFreeTier = !isPremium;

  // Billing toggle state — starts in the user's actual billing period
  const [billingPeriod, setBillingPeriod] = useState(
    currentBillingPeriod === 'monthly' ? 'monthly' : 'annually'
  );

  // Sync the toggle whenever the modal opens so a monthly user sees "Monthly"
  // by default; if they flip it, the current-plan card switches to "Switch to …".
  useEffect(() => {
    if (isOpen) {
      setBillingPeriod(
        currentBillingPeriod === 'monthly' ? 'monthly' : 'annually'
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && isPremium && onRefreshSubscription) {
      onRefreshSubscription();
    }
  }, [isOpen, isPremium, onRefreshSubscription]);

  // ── Build tier cards ───────────────────────────────────────
  //   Non-premium → [Free, Starter, Professional, Groups] (4 cols)
  //   Premium     → [Starter, Professional, Groups]       (3 cols)
  const tierCards = useMemo(() => {
    const order = isPremium
      ? PAID_TIER_ORDER
      : [FREE_TIER_KEY, ...PAID_TIER_ORDER];

    return order.map((key) => {
      // Free plan (static, ₹0/month)
      if (key === FREE_TIER_KEY) {
        return {
          key,
          name: FREE_PLAN.name,
          description: FREE_PLAN.description,
          price: FREE_PLAN.price,
          savings: 0,
          popular: false,
          isGroup: false,
          isFree: true,
          features: FREE_PLAN.features,
        };
      }

      // Group plan uses static config from GROUPS_PLAN
      if (key === GROUPS_PLAN.key) {
        return {
          key,
          name: GROUPS_PLAN.name,
          description: GROUPS_PLAN.description,
          price: null, // no numeric price — custom
          priceLabel: GROUPS_PLAN.columnPriceLabel,
          savings: 0,
          popular: false,
          isGroup: true,
          ctaLabel: GROUPS_PLAN.ctaLabel,
          featuresHeader: GROUPS_PLAN.featuresHeader,
          features: GROUPS_PLAN.features,
        };
      }

      const meta = getPlanMeta?.(key, billingPeriod);
      const name =
        meta?.displayName?.replace(/\s*\([^)]*\)\s*$/, '').trim() ||
        key.charAt(0).toUpperCase() + key.slice(1);
      const savings = computeAnnualSavingsPercent(
        getAmount(key, 'monthly', 1),
        getAmount(key, 'annually', 1)
      );
      return {
        key,
        name,
        description: getDescription(key, billingPeriod),
        price: getAmount(key, billingPeriod, 1),
        savings,
        popular: key === DEFAULT_PLAN_KEY,
        isGroup: false,
        isFree: false,
        features: TIER_FEATURES[key] || [],
      };
    });
  }, [billingPeriod, getAmount, getDescription, getPlanMeta, isPremium]);

  // ── Trial info (non-premium only) ──────────────────────────
  const trialDays = subscriptionInfo?.trialDays || 7;
  const trialDaysRemaining =
    typeof subscriptionInfo?.daysRemaining === 'number'
      ? subscriptionInfo.daysRemaining
      : null;

  const hasActiveTrial =
    !isPremium &&
    (subscriptionInfo?.isTrial === true ||
      (trialDaysRemaining != null &&
        trialDaysRemaining >= 0 &&
        (subscriptionInfo?.trialStarted || subscriptionInfo?.trialStartedAt)));

  const trialEnded = hasActiveTrial && trialDaysRemaining === 0;
  const trialDaysUsed =
    trialDaysRemaining != null ? Math.max(0, trialDays - trialDaysRemaining) : 0;
  const trialProgressPct =
    trialDaysRemaining != null
      ? Math.min(
          100,
          Math.max(0, ((trialDays - trialDaysRemaining) / trialDays) * 100)
        )
      : 0;

  const trialChartsUsed = subscriptionInfo?.chartsUsed ?? 0;
  const trialMaxCharts =
    subscriptionInfo?.maxChartsPerDay || subscriptionInfo?.maxCharts || 25;

  // ── Handlers ───────────────────────────────────────────────
  const handleSubscribe = (planType) => {
    onSubscribe?.(planType, billingPeriod, 1);
    sessionStorage.setItem(
      'vbot_payment_plan',
      JSON.stringify({ planKey: planType, billingPeriod, seatCount: 1 })
    );
    if (isPremium) {
      sessionStorage.setItem('vbot_payment_intent', 'change_plan');
    }
    onClose?.();
    navigate('/payment');
  };

  // Switch billing period on the user's CURRENT plan (monthly ⇄ annually)
  const handleSwitchBilling = () => {
    sessionStorage.setItem(
      'vbot_payment_plan',
      JSON.stringify({
        planKey: currentPlanKey,
        billingPeriod,
        seatCount: subscriptionInfo?.seatCount || 1,
      })
    );
    sessionStorage.setItem(
      'vbot_payment_intent',
      billingPeriod === 'annually' ? 'switch_to_annual' : 'switch_to_monthly'
    );
    onClose?.();
    navigate('/payment');
  };

  // Group plan goes to Talk-to-sales on the public site (different domain).
  // It opens in a new tab, so close the modal here to leave the app tab clean.
  const handleGroupClick = () => {
    onClose?.();
    if (onContactSales) {
      onContactSales();
    } else {
      goToTalkToSales();
    }
  };

  // ── Billing toggle (shared by both views) ──────────────────
  //   Pill-style segmented switch. The "Annually" segment carries a
  //   "Save 15%" badge so users immediately see the incentive.
  const billingToggle = (
    <div className="flex items-center justify-center mt-4">
      <div
        role="tablist"
        aria-label="Billing period"
        className="inline-flex items-center bg-gray-100 rounded-full p-1"
      >
        <button
          type="button"
          role="tab"
          aria-selected={billingPeriod === 'monthly'}
          onClick={() => setBillingPeriod('monthly')}
          className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
            billingPeriod === 'monthly'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Monthly
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={billingPeriod === 'annually'}
          onClick={() => setBillingPeriod('annually')}
          className={`flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
            billingPeriod === 'annually'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <span>Annually</span>
          <span
            className={`text-xs font-semibold px-1.5 py-0.5 rounded-full transition-colors ${
              billingPeriod === 'annually'
                ? 'bg-teal-100 text-teal-800'
                : 'bg-teal-50 text-teal-700'
            }`}
          >
            Save 15%
          </span>
        </button>
      </div>
    </div>
  );

  // ── Trial banner (non-premium + trialing only) ─────────────
  const trialCard = hasActiveTrial ? (
    <div className="mx-6 mt-5 rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 px-4 py-4">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center flex-shrink-0">
          {trialEnded ? (
            <Sparkles className="w-5 h-5 text-white" />
          ) : (
            <Clock className="w-5 h-5 text-white" />
          )}
        </div>
        <div className="text-left min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-semibold text-gray-900">
              {trialEnded ? 'Your free trial has ended' : 'Free trial active'}
            </p>
            <Badge
              className={
                trialEnded
                  ? 'bg-rose-100 text-rose-800 border-rose-200'
                  : 'bg-amber-100 text-amber-800 border-amber-200'
              }
            >
              {trialEnded
                ? 'Trial ended'
                : trialDaysRemaining != null
                  ? `${trialDaysRemaining} day${
                      trialDaysRemaining === 1 ? '' : 's'
                    } left`
                  : `${trialDays} days`}
            </Badge>
          </div>
          <p className="text-xs text-gray-600 mt-0.5">
            {trialEnded
              ? 'Upgrade to a paid plan to keep creating charts and notes.'
              : `${trialDays}-day Professional trial · Upgrade anytime to keep unlimited access.`}
          </p>
        </div>
      </div>

      {trialDaysRemaining != null && (
        <div className="mb-3">
          <div className="w-full h-1.5 rounded-full bg-amber-100 overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-amber-500 to-orange-500 transition-all"
              style={{ width: `${trialProgressPct}%` }}
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {trialEnded
              ? `Completed ${trialDays}-day trial`
              : `Day ${trialDaysUsed} of ${trialDays}`}
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-lg bg-white/70 px-3 py-2">
          <p className="text-xs uppercase tracking-wide text-gray-500">
            Charts today
          </p>
          <p className="text-sm font-medium text-gray-900">
            {trialChartsUsed} / {trialMaxCharts}
          </p>
        </div>
        <div className="rounded-lg bg-white/70 px-3 py-2">
          <p className="text-xs uppercase tracking-wide text-gray-500">
            Current plan
          </p>
          <p className="text-sm font-medium text-gray-900">Free (trial)</p>
        </div>
      </div>
    </div>
  ) : null;

  // ── Plan grid (shared by both views) ───────────────────────
  //   Premium users now see ONLY the grid (no summary panel above it).
  //   The current-plan card reacts to the billing toggle:
  //     • Toggle == user's actual billing  → gray "Your current plan"
  //     • Toggle != user's actual billing  → gradient "Switch to annual/monthly"
  const gridCols = isPremium
    ? 'md:grid-cols-3'
    : 'md:grid-cols-2 lg:grid-cols-4';

  const planGrid = (
    <div className={`grid ${gridCols} gap-4 px-6 pb-6 pt-2 items-stretch`}>
      {tierCards.map((tier) => {
        // ── current-plan detection ──
        const isSamePlanKey =
          isPremium && !tier.isGroup && tier.key === currentPlanKey;

        const isSameBilling =
          isSamePlanKey && billingPeriod === currentBillingPeriod;

        // Truly current (same plan + same billing)
        const isCurrentPlan = tier.isGroup
          ? false
          : tier.isFree
            ? isOnFreeTier
            : isSameBilling;

        // Same plan but user flipped the toggle → allow switching billing period
        const isSamePlanDifferentBilling =
          isSamePlanKey && !isSameBilling;

        const paidIndex = PAID_TIER_ORDER.indexOf(tier.key);
        const currentPaidIndex = PAID_TIER_ORDER.indexOf(currentPlanKey);
        const isUpgrade =
          isPremium && !tier.isFree && paidIndex > currentPaidIndex;

        const shortLabel =
          tier.key === GROUPS_PLAN.key
            ? tier.name
            : PLAN_SHORT_LABEL[tier.key] || tier.name;

        const showPopular = !isPremium && tier.popular;

        // ── CTA label ──
        let buttonLabel;
        if (isCurrentPlan) {
          buttonLabel = 'Your current plan';
        } else if (tier.isGroup) {
          buttonLabel = GROUPS_PLAN.ctaLabel;
        } else if (tier.isFree) {
          buttonLabel = 'Switch to Free';
        } else if (isSamePlanDifferentBilling) {
          buttonLabel =
            billingPeriod === 'annually'
              ? 'Switch to annual'
              : 'Switch to monthly';
        } else if (isPremium) {
          buttonLabel = isUpgrade
            ? `Upgrade to ${shortLabel}`
            : `Switch to ${shortLabel}`;
        } else {
          buttonLabel = tier.popular ? 'Upgrade to Pro' : 'Upgrade to Starter';
        }

        // ── Click handler ──
        const handleCardClick = () => {
          if (isCurrentPlan) return;
          if (tier.isGroup) {
            handleGroupClick();
          } else if (tier.isFree) {
            onClose?.();
          } else if (isSamePlanDifferentBilling) {
            handleSwitchBilling();
          } else {
            handleSubscribe(tier.key);
          }
        };

        return (
          <div
            key={tier.key}
            aria-current={isCurrentPlan ? 'true' : undefined}
            className={`relative border rounded-lg p-5 flex flex-col h-full bg-white transition-shadow ${
              isCurrentPlan
                ? 'border-2 border-gray-900 shadow-sm'
                : showPopular
                  ? 'border-blue-500 shadow-md'
                  : 'border-gray-200'
            }`}
          >
            {/* Top badge — Current / Most popular */}
            {(isCurrentPlan || showPopular) && (
              <div className="absolute -top-2.5 left-4">
                {isCurrentPlan ? (
                  <Badge className="bg-gray-900 text-white border-gray-900 shadow-sm">
                    Your current plan
                  </Badge>
                ) : (
                  <Badge className="bg-blue-600 text-white border-blue-600 shadow-sm">
                    Most popular
                  </Badge>
                )}
              </div>
            )}

            <div className="text-center mt-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                {tier.name}
              </h3>
              {tier.description && (
                <p className="text-xs text-gray-600 mb-3 px-1 min-h-[2.5rem] leading-snug">
                  {tier.description}
                </p>
              )}
              <div className="flex items-center justify-center gap-2 mb-4 flex-wrap min-h-[2.5rem]">
                {/* Group plan shows "Custom" instead of a numeric price */}
                {tier.isGroup ? (
                  <span className="text-3xl font-bold text-gray-900">
                    {GROUPS_PLAN.priceLabel}
                  </span>
                ) : (
                  <>
                    <span className="text-3xl font-bold text-gray-900">
                      {CURRENCY_SYMBOL}
                      {tier.price ?? '—'}
                    </span>
                    <span className="text-gray-600 text-sm">/mo*</span>
                    {billingPeriod === 'annually' &&
                      !tier.isFree &&
                      tier.savings > 0 &&
                      !isCurrentPlan && (
                        <Badge className="bg-teal-100 text-teal-800 border-teal-200">
                          Save {tier.savings}%
                        </Badge>
                      )}
                  </>
                )}
              </div>
            </div>

            <Button
              type="button"
              aria-disabled={isCurrentPlan || undefined}
              onClick={handleCardClick}
              className={`w-full h-11 text-base font-medium mb-4 ${
                isCurrentPlan ? CURRENT_BUTTON : CTA_GRADIENT
              }`}
            >
              {buttonLabel}
            </Button>

            {/* Features section - hidden for Group plan (nothing after Talk to Sales button) */}
            {!tier.isGroup && (
              <div className="space-y-2.5 pt-4 border-t border-gray-100">
                {tier.features.map((feature) => (
                  <div key={feature} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-teal-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700 leading-snug">
                      {feature}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  // ── PREMIUM (already paid) view — 3 cols, grid only ────────
  if (isPremium) {
    return (
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose?.()}>
        <DialogContent className="w-[95vw] max-w-5xl max-h-[90vh] overflow-y-auto p-0 gap-0 rounded-xl pb-[env(safe-area-inset-bottom)]">
          <DialogHeader className="text-center pt-5 pb-2 px-4 sm:px-6 sm:pr-12">
            <DialogTitle className="text-2xl font-bold text-gray-900">
              Manage your subscription
            </DialogTitle>
            <p className="text-sm text-gray-600 mt-1">
              You&apos;re on the{' '}
              <span className="font-semibold text-gray-900">
                {tierCards.find((t) => t.key === currentPlanKey)?.name ||
                  'Premium'}
              </span>{' '}
              plan. Change or upgrade anytime.
            </p>
            {billingToggle}
          </DialogHeader>

          {planGrid}
        </DialogContent>
      </Dialog>
    );
  }

  // ── NON-PREMIUM (subscribe / trial) view — 4 cols ──────────
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose?.()}>
      <DialogContent className="w-[95vw] max-w-6xl max-h-[90vh] overflow-y-auto p-0 gap-0 rounded-xl pb-[env(safe-area-inset-bottom)]">
        <DialogHeader className="text-center pt-5 pb-4 px-4 sm:px-6 sm:pr-12">
          <DialogTitle className="text-2xl font-bold text-gray-900">
            {hasActiveTrial ? 'Upgrade your plan' : 'Subscribe to VirtualScribe'}
          </DialogTitle>
          {billingToggle}
        </DialogHeader>

        {/* Trial banner (non-premium + trialing only) */}
        {trialCard}

        {planGrid}
      </DialogContent>
    </Dialog>
  );
};

export default VBotSubscriptionModal;